import { useCallback, useEffect, useRef, useState } from 'react'
import {
  IconLoading,
  IconNext,
  IconPlay,
  IconPrevious,
  IconStop,
  IconSpotify,
  IconVolume,
  IconVolumeMuted,
  PlayerIconButton,
} from './PlayerIcons'
import { fetchWebsitePlaylist } from '../lib/playlistData'
import { emitMusicPlayback } from '../lib/hubTools'
import { CHARACTER_CLIPS } from '../data/characterClips'
import { loadingAttrs, useAssetReady } from '../lib/hooks/useSceneReadiness'
import { IS_OWNER } from '../lib/ownerMode'

const DANCE_ASSET_IDS = [
  CHARACTER_CLIPS.sakira.dance.assetId,
  CHARACTER_CLIPS.song.dance.assetId,
]

const stripFeaturing = (title) =>
  title.replace(/\s*\(feat\.[^)]+\)/gi, '').trim()

const searchItunesPreviews = async (term) => {
  const query = encodeURIComponent(term)
  const response = await fetch(
    `https://itunes.apple.com/search?term=${query}&limit=5&media=music&entity=song`
  )

  if (!response.ok) return []

  const data = await response.json()
  return data.results ?? []
}

/** Only used when a track has no pinned previewUrl in playlist.json. */
const fetchPreviewUrlFallback = async (title, artist) => {
  const primaryArtist = artist.split(',')[0]?.trim() ?? artist
  const simplifiedTitle = stripFeaturing(title)
  const queries = [
    `${title} ${artist}`,
    `${simplifiedTitle} ${artist}`,
    `${title} ${primaryArtist}`,
    `${simplifiedTitle} ${primaryArtist}`,
  ]

  const seen = new Set()

  for (const term of queries) {
    if (!term || seen.has(term)) continue
    seen.add(term)

    try {
      const results = await searchItunesPreviews(term)
      const match = results.find((result) => result.previewUrl)
      if (match?.previewUrl) return match.previewUrl
    } catch (error) {
      if (IS_OWNER) console.warn('[playlist] iTunes fallback lookup failed:', term, error)
    }
  }

  return null
}

const resolveTrackPreviewUrl = async (track) => {
  if (!track) return null
  if (track.previewUrl) return track.previewUrl
  if (!track.hasPreview) return null
  return fetchPreviewUrlFallback(track.title, track.artist)
}

const waitForAudioReady = (audio) =>
  new Promise((resolve, reject) => {
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      resolve()
      return
    }

    const cleanup = () => {
      audio.removeEventListener('canplay', onReady)
      audio.removeEventListener('error', onError)
    }

    const onReady = () => {
      cleanup()
      resolve()
    }

    const onError = () => {
      cleanup()
      reject(new Error('Audio preview failed to load'))
    }

    audio.addEventListener('canplay', onReady)
    audio.addEventListener('error', onError)
  })

const mayaRulerMarks = [0, 100, 200, 300, 400, 500]
const mayaFrameMax = 500

const getMayaFrame = (currentTime, duration) => {
  if (!duration) return 0
  return Math.round((currentTime / duration) * 500)
}

const PlaylistPlayer = () => {
  const audioRef = useRef(null)
  const previewCacheRef = useRef({})
  const volumeBeforeMuteRef = useRef(0.7)
  /** True after the user mutes — auto-advance must not unmute on the next track. */
  const userMutedRef = useRef(false)
  const shouldResumeOnLoadRef = useRef(false)
  const isTrackSwitchRef = useRef(false)
  const pendingPlayRef = useRef(false)
  const playlistRevisionRef = useRef(0)
  const [playlistLoading, setPlaylistLoading] = useState(true)
  const [playlistError, setPlaylistError] = useState(null)
  const [spotifyUrl, setSpotifyUrl] = useState('')
  const [tracks, setTracks] = useState([])
  const [trackIndex, setTrackIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.7)
  const [previewUrl, setPreviewUrl] = useState(null)
  const danceReady = useAssetReady(DANCE_ASSET_IDS)
  const danceLoading = !danceReady.ready || danceReady.loading

  const currentTrack = tracks[trackIndex] ?? null
  const trackCount = tracks.length
  const canPlayCurrentTrack = Boolean(currentTrack?.previewUrl)

  useEffect(() => {
    let isActive = true

    const loadPlaylist = async () => {
      setPlaylistLoading(true)
      setPlaylistError(null)

      try {
        const playlist = await fetchWebsitePlaylist()
        if (!isActive) return

        playlistRevisionRef.current += 1
        previewCacheRef.current = {}
        setSpotifyUrl(playlist.spotifyUrl)
        setTracks(playlist.tracks)
        setTrackIndex(0)
        setPreviewUrl(null)
        setCurrentTime(0)
        setDuration(0)
        setIsPlaying(false)
        pendingPlayRef.current = false
        shouldResumeOnLoadRef.current = false
        isTrackSwitchRef.current = false

        if (audioRef.current) {
          audioRef.current.pause()
          audioRef.current.removeAttribute('src')
          audioRef.current.load()
        }
      } catch (error) {
        if (IS_OWNER) console.warn('[playlist] failed to load playlist.json:', error)
        if (!isActive) return
        setPlaylistError('Playlist unavailable')
        setTracks([])
        setSpotifyUrl('')
      } finally {
        if (isActive) setPlaylistLoading(false)
      }
    }

    loadPlaylist()

    return () => {
      isActive = false
    }
  }, [])

  useEffect(() => {
    if (trackIndex >= tracks.length) {
      setTrackIndex(tracks.length ? 0 : 0)
    }
  }, [trackIndex, tracks.length])

  useEffect(() => {
    previewCacheRef.current = {}
  }, [tracks])

  const prevPlayingRef = useRef(isPlaying)
  useEffect(() => {
    if (prevPlayingRef.current === isPlaying) return
    // Auto-advance keeps isPlaying true; ignore pause flicker at track end
    if (
      !isPlaying
      && (shouldResumeOnLoadRef.current || isTrackSwitchRef.current)
    ) {
      return
    }
    prevPlayingRef.current = isPlaying
    const track = tracks[trackIndex]
    emitMusicPlayback({
      playing: isPlaying,
      title: track?.title ?? '',
      artist: track?.artist ?? '',
      trackIndex,
    })
  }, [isPlaying, trackIndex, tracks])

  const loadPreview = useCallback(async (index) => {
    const track = tracks[index]
    if (!track) return null
    if (!track.previewUrl && !track.hasPreview) return null

    const cacheKey = `${track.id}-${track.title}-${track.artist}`
    if (previewCacheRef.current[cacheKey]) {
      return previewCacheRef.current[cacheKey]
    }

    const url = await resolveTrackPreviewUrl(track)
    if (url) {
      previewCacheRef.current[cacheKey] = url
    }
    return url
  }, [tracks])

  const unmuteForPlayback = () => {
    if (userMutedRef.current || !isMuted) return

    const restoredVolume = volumeBeforeMuteRef.current || 0.7
    setVolume(restoredVolume)
    setIsMuted(false)

    if (audioRef.current) {
      audioRef.current.muted = false
      audioRef.current.volume = restoredVolume
    }
  }

  const startPlaybackRef = useRef(async () => false)

  startPlaybackRef.current = async () => {
    const audio = audioRef.current
    if (!audio?.src) return false

    try {
      await waitForAudioReady(audio)
      unmuteForPlayback()
      await audio.play()
      setIsPlaying(true)
      pendingPlayRef.current = false
      return true
    } catch (error) {
      if (IS_OWNER) console.warn('[playlist] playback failed:', error)
      setIsPlaying(false)
      pendingPlayRef.current = false
      return false
    }
  }

  const startPlayback = () => startPlaybackRef.current()

  useEffect(() => {
    if (playlistLoading || !tracks.length) return

    let isActive = true

    const loadTrack = async () => {
      setIsLoading(true)
      const resumeAfterLoad = shouldResumeOnLoadRef.current
      // Keep isPlaying true across next/prev so dance / playlist consumers don't drop out
      if (!resumeAfterLoad) {
        setIsPlaying(false)
      }
      setCurrentTime(0)
      setDuration(0)

      let url = null

      try {
        url = await loadPreview(trackIndex)
      } catch (error) {
        if (IS_OWNER) console.warn('[playlist] preview load failed:', error)
      }

      if (!isActive) return

      setPreviewUrl(url)
      setIsLoading(false)

      const shouldPlay = resumeAfterLoad || pendingPlayRef.current

      if (audioRef.current && url) {
        isTrackSwitchRef.current = resumeAfterLoad
        audioRef.current.src = url
        audioRef.current.load()

        if (shouldPlay) {
          shouldResumeOnLoadRef.current = false
          pendingPlayRef.current = false
          isTrackSwitchRef.current = resumeAfterLoad
          await startPlaybackRef.current()
          isTrackSwitchRef.current = false
        } else {
          shouldResumeOnLoadRef.current = false
          isTrackSwitchRef.current = false
        }
      } else {
        shouldResumeOnLoadRef.current = false
        isTrackSwitchRef.current = false
        pendingPlayRef.current = false
        if (resumeAfterLoad) setIsPlaying(false)
      }
    }

    loadTrack()

    return () => {
      isActive = false
    }
  }, [loadPreview, playlistLoading, trackIndex, tracks.length])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted
      audioRef.current.volume = volume
    }
  }, [isMuted, volume, previewUrl])

  const handleVolumeToggle = () => {
    if (isMuted) {
      userMutedRef.current = false
      const restoredVolume = volumeBeforeMuteRef.current || 0.7
      setVolume(restoredVolume)
      setIsMuted(false)
      return
    }

    userMutedRef.current = true
    volumeBeforeMuteRef.current = volume
    setIsMuted(true)
  }

  const handlePrevious = () => {
    shouldResumeOnLoadRef.current = isPlaying
    setTrackIndex((index) => (index === 0 ? trackCount - 1 : index - 1))
  }

  const handleNext = () => {
    shouldResumeOnLoadRef.current = isPlaying
    setTrackIndex((index) => (index === trackCount - 1 ? 0 : index + 1))
  }

  const handlePlayPause = async () => {
    const audio = audioRef.current
    if (!audio || !canPlayCurrentTrack) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      pendingPlayRef.current = false
      return
    }

    pendingPlayRef.current = true

    if (!previewUrl || !audio.src) {
      if (isLoading) return

      setIsLoading(true)

      try {
        const url = await loadPreview(trackIndex)
        setPreviewUrl(url)

        if (!url) {
          pendingPlayRef.current = false
          return
        }

        audio.src = url
        audio.load()
      } catch (error) {
        if (IS_OWNER) console.warn('[playlist] preview fetch failed:', error)
        pendingPlayRef.current = false
        return
      } finally {
        setIsLoading(false)
      }
    }

    await startPlayback()
  }

  const handleTimeChange = (event) => {
    const audio = audioRef.current
    if (!audio) return

    const nextTime = Number(event.target.value)
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const handleVolumeChange = (event) => {
    const nextVolume = Number(event.target.value)
    setVolume(nextVolume)
    volumeBeforeMuteRef.current = nextVolume

    if (nextVolume === 0) {
      userMutedRef.current = true
      setIsMuted(true)
      return
    }

    userMutedRef.current = false
    setIsMuted(false)
  }

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const isAtTrackEnd = (audio) => {
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return false
    return audio.ended || audio.currentTime >= audio.duration - 0.25
  }

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleAudioEnded = () => {
    shouldResumeOnLoadRef.current = true
    isTrackSwitchRef.current = true
    setTrackIndex((index) => (index === trackCount - 1 ? 0 : index + 1))
  }

  const progressPercent = duration ? (currentTime / duration) * 100 : 0
  const mayaFrame = getMayaFrame(currentTime, duration)

  return (
    <div className="playlist-player site-frame min-w-0">
      <p className="playlist-player-track site-panel-subtitle min-w-0 truncate normal-case">
        {playlistError ? (
          <span className="site-muted">{playlistError}</span>
        ) : currentTrack ? (
          <>
            <span className="text-text-primary">{currentTrack.title}</span>
            <span className="site-muted"> — {currentTrack.artist}</span>
            {!canPlayCurrentTrack ? (
              <span className="site-muted"> (no preview)</span>
            ) : null}
          </>
        ) : (
          <span className="site-muted">Loading playlist…</span>
        )}
      </p>

      <audio
        ref={audioRef}
        preload="none"
        muted={isMuted}
        onTimeUpdate={handleAudioTimeUpdate}
        onLoadedMetadata={handleAudioLoadedMetadata}
        onEnded={handleAudioEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => {
          // src/load during next/prev/end-of-track fires pause — keep session alive
          if (isTrackSwitchRef.current || shouldResumeOnLoadRef.current) return
          const audio = audioRef.current
          if (isAtTrackEnd(audio)) return
          setIsPlaying(false)
        }}
      />

      <div className="playlist-player-timeline min-w-0">
        <div className="playlist-maya-scrubber min-w-0 flex-1">
          <span className="playlist-maya-frame">{mayaFrame}</span>

          <div className="playlist-player-timeline-track relative min-w-0 flex-1">
            <div className="playlist-maya-ruler" aria-hidden="true">
              {mayaRulerMarks.map((mark) => (
                <span key={mark} className="playlist-maya-ruler-mark">
                  {mark}
                </span>
              ))}
            </div>
            <div className="playlist-player-timeline-ruler" aria-hidden="true" />
            <div
              className="playlist-maya-playhead"
              style={{ left: `${progressPercent}%` }}
              aria-hidden="true"
            />
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.1"
              value={currentTime}
              onChange={handleTimeChange}
              disabled={!previewUrl || !duration}
              className="playlist-range playlist-range-timeline min-w-0 w-full"
              aria-label="Track progress"
            />
          </div>

          <span className="playlist-maya-frame playlist-maya-frame-end">{mayaFrameMax}</span>

          <div className="playlist-player-controls playlist-player-controls-scrubber shrink-0 items-center gap-0.5">
            <PlayerIconButton label="Previous track" onClick={handlePrevious} disabled={playlistLoading || !trackCount}>
              <IconPrevious />
            </PlayerIconButton>
            <PlayerIconButton
              label={
                !canPlayCurrentTrack
                  ? 'Preview unavailable'
                  : isPlaying
                    ? 'Stop track'
                    : 'Play track'
              }
              onClick={handlePlayPause}
              disabled={playlistLoading || !canPlayCurrentTrack || danceLoading}
              {...loadingAttrs(danceLoading)}
            >
              {playlistLoading || isLoading ? (
                <IconLoading />
              ) : isPlaying ? (
                <IconStop />
              ) : (
                <IconPlay />
              )}
            </PlayerIconButton>
            <PlayerIconButton label="Next track" onClick={handleNext} disabled={playlistLoading || !trackCount}>
              <IconNext />
            </PlayerIconButton>
          </div>
        </div>
      </div>

      <div className="playlist-player-volume flex shrink-0 items-center gap-1">
        <PlayerIconButton
          label={isMuted ? 'Unmute' : 'Mute'}
          onClick={handleVolumeToggle}
          className={isMuted ? 'text-text-muted' : ''}
        >
          {isMuted ? <IconVolumeMuted /> : <IconVolume />}
        </PlayerIconButton>
        <div className="hidden w-12 min-w-0 items-center lg:flex">
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="playlist-range w-full"
            aria-label="Volume"
          />
        </div>
      </div>

      <a
        href={spotifyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="playlist-player-spotify flex h-7 w-7 shrink-0 items-center justify-center text-text-muted transition-opacity hover:text-text-primary hover:opacity-70"
        aria-label="Open playlist in Spotify"
      >
        <IconSpotify />
      </a>
    </div>
  )
}

export default PlaylistPlayer
